/**
 * The three things a control can be, and nothing else.
 *
 * There were thirty-three hand-written buttons across the product and no
 * primitive: every one assembled its own padding, radius, hover and focus out
 * of utilities, so no two agreed and none of them looked designed. That is the
 * whole reason the interface read as a template — a template is what you get
 * when the same decision is made thirty-three times by hand.
 *
 * Three roles, because a screen has at most three ranks of action:
 *
 *   - `primary` — the one thing this screen is for. Solid accent, dark ink.
 *     One per region; two primaries is none.
 *   - `secondary` — a real action that is not the point. Filled neutral.
 *   - `ghost` — navigation and toggles, where a filled control would compete
 *     with the figures it sits beside.
 *
 * Every role carries hover, active, disabled and a visible focus ring, because
 * a control that does not answer the pointer is the second thing that makes an
 * interface feel unfinished.
 */

export type ButtonVariant = "primary" | "secondary" | "ghost";
export type ButtonSize = "sm" | "md" | "icon" | "cell";

const BASE =
  "inline-flex items-center justify-center gap-1.5 rounded-row font-medium " +
  "cursor-pointer transition-colors duration-150 " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 " +
  "focus-visible:outline-focus-ring " +
  "disabled:cursor-not-allowed disabled:opacity-50";

/**
 * The look of the option a segmented control is currently on.
 *
 * Not a fourth variant: "selected" is a state of a ghost control, and a tab
 * that changed its whole identity when chosen would read as a different
 * control appearing where the old one was.
 */
const SELECTED = "bg-surface-selected font-medium text-text-primary";

const VARIANT: Record<ButtonVariant, string> = {
  // Dark ink on the accent, never white: the accent is a light amber and white
  // on it reads at 1.8:1.
  primary:
    "bg-action-primary text-text-on-action hover:bg-action-hover " +
    "active:brightness-95",
  secondary:
    "bg-surface-hover text-text-primary hover:bg-surface-selected " +
    "active:brightness-95",
  ghost:
    "bg-transparent text-text-secondary hover:bg-surface-hover " +
    "hover:text-text-primary",
};

const SIZE: Record<ButtonSize, string> = {
  // 28px and 34px tall. Both clear the pointer target the baseline asks for
  // once the row spacing around them is counted.
  sm: "min-h-7 px-2.5 text-helper-sm",
  md: "min-h-9 px-4 text-body-main",
  // Square, and its own size rather than a caller overriding the padding.
  // `px-0` in a className does not beat `px-4` from here — Tailwind orders
  // both by value in the sheet, not by where they sit in the attribute — so a
  // 36px control ended up with 32px of padding and a four-pixel icon.
  icon: "size-9 p-0 pointer-coarse:size-11",
  // A column head. No horizontal padding at all: the head sits over a column
  // of right-aligned figures, and ten pixels of button padding puts every
  // caption ten pixels off the numbers it names — which is what makes a table
  // read as a zigzag rather than as columns.
  cell: "min-h-7 p-0 text-label-caps",
};

type Props = {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** The current option of a segmented control, a tab strip or a filter. */
  readonly selected?: boolean;
  readonly ref?: React.Ref<HTMLButtonElement>;
  readonly children: React.ReactNode;
  readonly className?: string;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "className">;

export function Button({
  variant = "secondary",
  size = "md",
  selected = false,
  children,
  className = "",
  type = "button",
  ref,
  ...rest
}: Props): React.JSX.Element {
  return (
    <button
      ref={ref}
      type={type}
      className={`${BASE} ${VARIANT[variant]} ${SIZE[size]} ${
        selected ? SELECTED : ""
      } ${className}`
        .replace(/\s+/g, " ")
        .trim()}
      {...rest}
    >
      {children}
    </button>
  );
}
